/**
 * LumfiLogo — single source of truth for every Lumfi brand asset.
 *
 * The artwork lives in `/public/icons/` and was delivered in four
 * lock-up families. Each family exposes the same `variant` axis so
 * callers can pick the colour treatment that fits the surface they
 * are rendering on (light bg, dark bg, monochrome print, etc).
 *
 *   <LumfiMark />          — square icon (sidebar, mobile nav,
 *                            avatar slots, favicons).
 *   <LumfiWordmark />      — horizontal mark + "Lumfi" / "Lumfi ·
 *                            CASH PILOT" lock-up (auth headers,
 *                            marketing chrome, email signatures).
 *   <LumfiStacked />       — vertical lock-up; mark on top, name
 *                            below. Use it on splash / loading
 *                            states where vertical space is cheap.
 *   <LumfiWordOnly />      — just the "Lumfi" wordmark, no mark.
 *   <LumfiPayLockup />     — co-branded "Lumfi Pay · Pago seguro"
 *                            badge for checkout surfaces.
 *
 * All components accept `className` plus a size knob (`size` for the
 * square mark, `height` for the horizontal/stacked lock-ups). The
 * width auto-computes to preserve aspect ratio.
 */
import { cn } from "../lib/utils";

// ---------------------------------------------------------------------------
// LumfiMark — square icon.
//
// Variants:
//   • "app"       — rounded-square app icon (white card, dark glyph).
//                    Default; matches the favicon + PWA icon.
//   • "app-dark"  — rounded-square app icon, dark theme background.
//   • "light"     — transparent background, light-tinted glyph
//                    (use over the brand emerald background).
//   • "dark"      — transparent background, dark-tinted glyph
//                    (use over a light surface).
//   • "black"     — pure black glyph (print, monochrome contexts).
//   • "white"     — pure white glyph (use over photography or the
//                    deepest dark backgrounds).
// ---------------------------------------------------------------------------
const MARK_SOURCES = {
  app: "/icons/lumfi-app-icon-1024.png",
  "app-dark": "/icons/lumfi-app-icon-dark-1024.png",
  light: "/icons/lumfi-mark-light-1024.png",
  dark: "/icons/lumfi-mark-dark-1024.png",
  black: "/icons/lumfi-mark-black-1024.png",
  white: "/icons/lumfi-mark-white-1024.png",
};

export function LumfiMark({
  size = 32,
  variant = "app",
  className = "",
  alt = "Lumfi",
}) {
  const src = MARK_SOURCES[variant] || MARK_SOURCES.app;
  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      // The "app" / "app-dark" assets already include their own
      // rounded-square background so we never add `rounded-` here —
      // it would clip the corners.
      className={cn("shrink-0 select-none", className)}
      style={{ width: size, height: size }}
      draggable={false}
    />
  );
}

// ---------------------------------------------------------------------------
// LumfiWordmark — horizontal "mark + Lumfi" lock-up.
//
// Variants (background it sits on):
//   • "light"  — dark glyph & text, for light surfaces.   (default)
//   • "dark"   — light glyph & text, for dark surfaces.
//   • "black"  — monochrome black, print/email signature.
//   • "white"  — monochrome white, use over photography.
//
// `tagline`:
//   • `true`   — uses the "Lumfi · CASH PILOT" variant (only
//                available for light/dark backgrounds; the
//                monochrome assets ship without the tagline).
//   • `false`  — uses the plain "Lumfi" lock-up.
// ---------------------------------------------------------------------------
const WORDMARK_SOURCES = {
  // Plain mark + "Lumfi"
  plain: {
    // Convention: the suffix in the filename matches the background it
    // is designed to sit on. So `*-light` = artwork tuned for a light
    // surface (darker glyph/text), `*-dark` = artwork tuned for a dark
    // surface (lighter glyph/text).
    light: "/icons/lumfi-logo-horizontal-light-1440w.png",
    dark: "/icons/lumfi-logo-horizontal-dark-1440w.png",
    black: "/icons/lumfi-logo-horizontal-black-1440w.png",
    white: "/icons/lumfi-logo-horizontal-white-1440w.png",
  },
  // Mark + "Lumfi · CASH PILOT"
  tagline: {
    light: "/icons/lumfi-cash-pilot-horizontal-light-1440w.png",
    dark: "/icons/lumfi-cash-pilot-horizontal-dark-1440w.png",
  },
};

export function LumfiWordmark({
  className = "",
  height = 48,
  variant = "light",
  tagline = true,
  alt = "Lumfi · Cash Pilot",
}) {
  // Fall back to plain when caller asks for tagline on a monochrome
  // variant we don't ship a tagline asset for.
  const family = tagline && WORDMARK_SOURCES.tagline[variant]
    ? "tagline"
    : "plain";
  const src = WORDMARK_SOURCES[family][variant] || WORDMARK_SOURCES.plain.light;
  return (
    <img
      src={src}
      alt={alt}
      className={cn("select-none", className)}
      style={{ height, width: "auto" }}
      draggable={false}
    />
  );
}

// ---------------------------------------------------------------------------
// LumfiStacked — vertical lock-up (mark on top, "Lumfi · CASH PILOT"
// underneath). Use on splash screens / large hero moments.
// Variants: "light" (light bg) | "dark" (dark bg).
// ---------------------------------------------------------------------------
const STACKED_SOURCES = {
  light: "/icons/lumfi-logo-stacked-light-1600.png",
  dark: "/icons/lumfi-logo-stacked-dark-1600.png",
};

export function LumfiStacked({
  className = "",
  height = 160,
  variant = "light",
  alt = "Lumfi · Cash Pilot",
}) {
  const src = STACKED_SOURCES[variant] || STACKED_SOURCES.light;
  return (
    <img
      src={src}
      alt={alt}
      className={cn("select-none", className)}
      style={{ height, width: "auto" }}
      draggable={false}
    />
  );
}

// ---------------------------------------------------------------------------
// LumfiWordOnly — just the "Lumfi" wordmark glyph, no mark.
// Useful inside dense headers where the mark would compete with
// other iconography (e.g. an avatar nav).
// Variants: "light" (dark text, light bg) | "dark" (light text, dark bg).
// ---------------------------------------------------------------------------
const WORDONLY_SOURCES = {
  light: "/icons/lumfi-wordmark-light-1024.png", // dark glyph → light bg
  dark: "/icons/lumfi-wordmark-dark-1024.png",   // light glyph → dark bg
};

export function LumfiWordOnly({
  className = "",
  height = 32,
  variant = "light",
  alt = "Lumfi",
}) {
  const src = WORDONLY_SOURCES[variant] || WORDONLY_SOURCES.light;
  return (
    <img
      src={src}
      alt={alt}
      className={cn("select-none", className)}
      style={{ height, width: "auto" }}
      draggable={false}
    />
  );
}

// ---------------------------------------------------------------------------
// LumfiPayLockup — co-branded "Lumfi Pay · Pago seguro" lock-up,
// designed to sit alongside the checkout shield. Variants follow
// the same light/dark convention.
// ---------------------------------------------------------------------------
const PAY_SOURCES = {
  light: "/icons/lumfi-pay-lockup-light-1440w.png", // dark text → light bg
  dark: "/icons/lumfi-pay-lockup-dark-1440w.png",   // light text → dark bg
};

export function LumfiPayLockup({
  className = "",
  height = 28,
  variant = "light",
  alt = "Lumfi Pay · Pago seguro",
}) {
  const src = PAY_SOURCES[variant] || PAY_SOURCES.light;
  return (
    <img
      src={src}
      alt={alt}
      className={cn("select-none", className)}
      style={{ height, width: "auto" }}
      draggable={false}
    />
  );
}
